import { useRef, useState } from "react";
import styled from "styled-components";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Bio } from "../../data/constants";
import { Section, Eyebrow, SectionTitle, SectionIntro, Head } from "../../studio/ui";
import { FiMail, FiPhone, FiMapPin, FiSend } from "react-icons/fi";
import { FaGithub, FaLinkedin } from "react-icons/fa";

/* ============================================================
   CONTACT — Divine Futurism. Frosted stained-glass form +
   celestial channel relics. web3forms backend preserved.
============================================================ */

const Grid = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 44px;
  align-items: start;

  @media ${({ theme }) => theme.bp.lg} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const Channels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Channel = styled.a`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  border-radius: 14px;
  background: ${({ theme }) => theme.glass};
  border: 1px solid ${({ theme }) => theme.border};
  backdrop-filter: blur(12px);
  text-decoration: none;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.borderHi};
    box-shadow: 0 0 30px -8px ${({ theme }) => theme.goldGlow};
    transform: translateY(-2px);
  }

  .ico {
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    display: grid;
    place-items: center;
    border-radius: 12px;
    font-size: 19px;
    color: ${({ theme }) => theme.gold};
    background: rgba(201, 168, 76, 0.08);
    border: 1px solid ${({ theme }) => theme.border};
    box-shadow: 0 0 22px -6px ${({ theme }) => theme.goldGlow};
  }

  .meta {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .k {
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textMuted};
  }

  .v {
    font-size: 15px;
    color: ${({ theme }) => theme.white};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Form = styled.form`
  position: relative;
  padding: 32px;
  border-radius: 18px;
  background: ${({ theme }) => theme.bgPanel};
  border: 1px solid ${({ theme }) => theme.borderHi};
  backdrop-filter: blur(16px);
  box-shadow: 0 24px 70px -28px ${({ theme }) => theme.violetGlow},
    0 0 50px -18px ${({ theme }) => theme.goldGlow};
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.borderHi},
      transparent
    );
  }

  @media ${({ theme }) => theme.bp.sm} {
    padding: 24px;
  }
`;

const Two = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media ${({ theme }) => theme.bp.sm} {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;

  label {
    font-family: ${({ theme }) => theme.fonts.serif};
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.white};
  }

  input,
  textarea {
    width: 100%;
    padding: 13px 15px;
    border-radius: 11px;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 15px;
    color: ${({ theme }) => theme.text};
    background: rgba(8, 6, 18, 0.6);
    border: 1px solid ${({ theme }) => theme.border};
    outline: none;
    resize: vertical;
    transition: border-color 0.25s ease, box-shadow 0.25s ease;

    &::placeholder {
      color: ${({ theme }) => theme.textFaint};
    }

    &:focus {
      border-color: ${({ theme }) => theme.gold};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.goldGlow};
    }
  }
`;

const Submit = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 4px;
  padding: 15px 24px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #0b0a14;
  background: linear-gradient(
    100deg,
    ${({ theme }) => theme.goldSoft},
    ${({ theme }) => theme.gold}
  );
  box-shadow: 0 0 28px -6px ${({ theme }) => theme.goldGlow};
  transition: transform 0.28s ease, box-shadow 0.28s ease, opacity 0.28s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 0 42px -4px ${({ theme }) => theme.goldGlow};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export default function Contact() {
  const form = useRef();
  const [busy, setBusy] = useState(false);

  const onSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    const formData = new FormData(event.target);
    formData.append("access_key", "42995784-74b4-498c-b6a2-66b26ad1dad7");
    const json = JSON.stringify(Object.fromEntries(formData));

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: json,
      }).then((r) => r.json());

      if (res.success) {
        toast.success("Message sent — I'll get back to you soon!", {
          position: "bottom-right",
        });
        form.current.reset();
      } else {
        toast.error("Something went wrong. Please try again.", {
          position: "bottom-right",
        });
      }
    } catch {
      toast.error("Something went wrong. Please try again.", {
        position: "bottom-right",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section id="contact">
      <Head>
        <Eyebrow>Contact</Eyebrow>
        <SectionTitle>
          Initiate <em>contact</em>
        </SectionTitle>
        <SectionIntro>
          I'm open to full-time roles and meaningful projects — send a message
          and I'll usually reply within a day.
        </SectionIntro>
      </Head>

      <Grid>
        <Channels>
          <Channel href={`mailto:${Bio.mail}`}>
            <span className="ico">
              <FiMail />
            </span>
            <div className="meta">
              <span className="k">Email</span>
              <span className="v">{Bio.mail}</span>
            </div>
          </Channel>

          <Channel href={`tel:${Bio.phone.replace(/\s/g, "")}`}>
            <span className="ico">
              <FiPhone />
            </span>
            <div className="meta">
              <span className="k">Phone</span>
              <span className="v">{Bio.phone}</span>
            </div>
          </Channel>

          <Channel as="div">
            <span className="ico">
              <FiMapPin />
            </span>
            <div className="meta">
              <span className="k">Location</span>
              <span className="v">{Bio.location}</span>
            </div>
          </Channel>

          <Channel href={Bio.linkedin} target="_blank" rel="noreferrer">
            <span className="ico">
              <FaLinkedin />
            </span>
            <div className="meta">
              <span className="k">LinkedIn</span>
              <span className="v">Connect with me</span>
            </div>
          </Channel>

          <Channel href={Bio.github} target="_blank" rel="noreferrer">
            <span className="ico">
              <FaGithub />
            </span>
            <div className="meta">
              <span className="k">GitHub</span>
              <span className="v">View my work</span>
            </div>
          </Channel>
        </Channels>

        <Form ref={form} onSubmit={onSubmit}>
          <Two>
            <Field>
              <label htmlFor="from_name">Name</label>
              <input
                id="from_name"
                name="from_name"
                placeholder="Your name"
                required
              />
            </Field>
            <Field>
              <label htmlFor="from_email">Email</label>
              <input
                id="from_email"
                type="email"
                name="from_email"
                placeholder="you@email.com"
                required
              />
            </Field>
          </Two>

          <Field>
            <label htmlFor="subject">Subject</label>
            <input
              id="subject"
              name="subject"
              placeholder="What's this about?"
              required
            />
          </Field>

          <Field>
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              rows="5"
              placeholder="Tell me about the role or project…"
              required
            />
          </Field>

          <Submit type="submit" disabled={busy}>
            <FiSend /> {busy ? "Sending…" : "Send Message"}
          </Submit>
        </Form>
      </Grid>

      <ToastContainer autoClose={5000} />
    </Section>
  );
}
